/**
 * PWA store composition (design doc §4.2). One RTK Query slice per mounted
 * bounded context; slices for UI state would be added here if a screen ever
 * needs cross-component UI state — per-intent draft ids live in the sheets
 * via `useDraftId`, and server state lives in the RTK Query cache, once.
 *
 * Every context a hook uses MUST be in `apis` — a hook whose api slice is not
 * mounted is a silent dead screen. And every mounted context that exports
 * invalidation rules must be registered — mounting and listening are two acts.
 */
import {
  createMfeStore,
  bindAuthStateReset,
  type RootStateOf,
  type AppDispatchOf
} from '@quickbee/store';
import { identityApi } from '@quickbee/api-identity';
// The Marketplace surface (network-mfe-design §8.4, §9 B14). ONE endpoint pair is called from
// here — `GET /commerce/storefront/listings` and its detail — under `commerce_storefront:read:own`,
// which is the only grant a SAVER holds in commerce.yml. The catalogue, the rate book, the coupons
// and the loyalty ledger stay closed to this app, and nothing here should ever call them.
import { commerceApi } from '@quickbee/api-commerce';
// The other half of the same surface (§8.4, §9 B11): at the OPERATOR's host the Marketplace tab
// reads the whole federation from marketplace-service instead of this shop's own offers, under
// `marketplace-storefront:read:own` — a SAVER's second grant on the platform, and its only one
// here. `useStorefrontListings` is the single place that chooses between the two; nothing else in
// this app should import either storefront hook directly.
import { marketplaceApi } from '@quickbee/api-marketplace';
import { schemeApi, registerSchemeInvalidation } from '@quickbee/api-scheme';
import { paymentApi, registerPaymentInvalidation } from '@quickbee/api-payment';
import { ledgerApi, registerLedgerInvalidation } from '@quickbee/api-ledger';
import { goalsApi, registerGoalsInvalidation } from '@quickbee/api-goals';
import { redemptionApi, registerRedemptionInvalidation } from '@quickbee/api-redemption';
import { disputeApi, registerDisputeInvalidation } from '@quickbee/api-dispute';
import { helpdeskApi, registerHelpdeskInvalidation } from '@quickbee/api-helpdesk';
import { pricingApi } from '@quickbee/api-pricing';
import { reportingApi } from '@quickbee/api-reporting';

const apis = [
  identityApi,
  commerceApi,
  marketplaceApi,
  schemeApi,
  paymentApi,
  ledgerApi,
  goalsApi,
  redemptionApi,
  disputeApi,
  helpdeskApi,
  pricingApi,
  reportingApi
] as const;

export const store = createMfeStore({ reducers: {}, apis });

bindAuthStateReset(store, apis);
registerSchemeInvalidation(store);
registerPaymentInvalidation(store);
registerLedgerInvalidation(store);
registerGoalsInvalidation(store);
registerRedemptionInvalidation(store);
registerDisputeInvalidation(store);
registerHelpdeskInvalidation(store);

export type RootState = RootStateOf<typeof store>;
export type AppDispatch = AppDispatchOf<typeof store>;
